'use client';

import { forwardRef, useCallback, useState, type DragEvent, type ReactNode } from 'react';
import Typography from '@mui/material/Typography';
import { styled, useThemeProps } from '@mui/material/styles';

import varnishTokens from '@allenai/varnish-theme/mui';

import { FileInputButton } from './FileInputButton';

interface FileInputProps {
    onSelect?: (files: FileList | null) => void;
    acceptedFileTypes?: string[];
    allowsMultiple?: boolean;
    icon?: ReactNode;
    defaultText?: string | ReactNode;
    variant?: 'contained' | 'outlined';
    size?: 'small' | 'medium' | 'large' | 'full';
    errorMessage?: string;
    className?: string;
    disabled?: boolean;
    children?: ReactNode;
}

interface FileInputOwnerState {
    variant: 'contained' | 'outlined';
    size: 'small' | 'medium' | 'large' | 'full';
    isDragOver: boolean;
    hasError: boolean;
}

const sizeStyles = {
    small: { padding: varnishTokens.spacing[2].value },
    medium: { padding: varnishTokens.spacing[3].value },
    large: { padding: varnishTokens.spacing[5].value },
    full: { padding: varnishTokens.spacing[3].value, width: '100%' },
} as const;

const FileInputRoot = styled('div', {
    name: 'MuiFileInput',
    slot: 'root',
})<{ ownerState: FileInputOwnerState }>(({ theme, ownerState }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    transition: 'border-color 0.2s, background-color 0.2s',
    ...sizeStyles[ownerState.size],

    ...(ownerState.variant === 'contained' && {
        backgroundColor: theme.palette.form.input.fill,
        border: `2px solid ${theme.palette.form.input.stroke}`,
    }),

    ...(ownerState.variant === 'outlined' && {
        backgroundColor: 'transparent',
        border: `2px dashed ${theme.palette.form.input.stroke}`,
    }),

    ...(ownerState.isDragOver && {
        borderColor: theme.palette.primary.main,
        backgroundColor:
            ownerState.variant === 'contained'
                ? theme.palette.action.hover
                : theme.palette.primary.main + '0A',
    }),

    ...(ownerState.hasError && {
        borderColor: theme.palette.error.main,
    }),
}));

function filterFiles(files: FileList, acceptedFileTypes?: string[]): FileList {
    if (!acceptedFileTypes || acceptedFileTypes.length === 0) {
        return files;
    }
    const dt = new DataTransfer();
    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (acceptedFileTypes.includes(file.type)) {
            dt.items.add(file);
        }
    }
    return dt.files;
}

const FileInput = forwardRef<HTMLDivElement, FileInputProps>(function FileInput(inProps, ref) {
    const props = useThemeProps({ props: inProps, name: 'MuiFileInput' });
    const {
        onSelect,
        acceptedFileTypes,
        allowsMultiple = false,
        icon,
        defaultText = 'Drag & Drop or browse',
        variant = 'outlined',
        size = 'medium',
        errorMessage,
        className,
        disabled,
        children,
    } = props;

    const [isDragOver, setIsDragOver] = useState(false);
    const [fileNames, setFileNames] = useState('');

    const ownerState: FileInputOwnerState = {
        variant,
        size,
        isDragOver,
        hasError: !!errorMessage,
    };

    const handleFiles = useCallback(
        (raw: FileList) => {
            let filtered = filterFiles(raw, acceptedFileTypes);

            if (!allowsMultiple && filtered.length > 1) {
                const dt = new DataTransfer();
                dt.items.add(filtered[0]);
                filtered = dt.files;
            }

            const names: string[] = [];
            for (let i = 0; i < filtered.length; i++) {
                names.push(filtered[i].name);
            }
            setFileNames(names.join(', '));
            onSelect?.(filtered);
        },
        [acceptedFileTypes, allowsMultiple, onSelect]
    );

    const handleDragOver = useCallback(
        (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            if (!disabled) {
                setIsDragOver(true);
            }
        },
        [disabled]
    );

    const handleDragLeave = useCallback(() => {
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(
        (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setIsDragOver(false);
            if (disabled) {
                return;
            }
            if (e.dataTransfer.files.length > 0) {
                handleFiles(e.dataTransfer.files);
            }
        },
        [disabled, handleFiles]
    );

    return (
        <FileInputRoot
            ref={ref}
            ownerState={ownerState}
            className={className}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}>
            {children || (
                <>
                    <FileInputButton
                        variant={variant === 'outlined' ? 'outlined' : 'contained'}
                        size={size === 'full' ? 'medium' : size}
                        onSelect={(files) => {
                            if (files) {
                                handleFiles(files);
                            }
                        }}
                        acceptedFileTypes={acceptedFileTypes}
                        allowsMultiple={allowsMultiple}
                        icon={icon}
                        buttonText={fileNames || defaultText}
                        disabled={disabled}
                    />
                    {fileNames ? (
                        <Typography variant="body2" color="text.secondary">
                            {fileNames}
                        </Typography>
                    ) : null}
                </>
            )}
            {errorMessage ? (
                <Typography variant="body2" color="error">
                    {errorMessage}
                </Typography>
            ) : null}
        </FileInputRoot>
    );
});

export { FileInput };
export type { FileInputProps };
